import { IRetailerLaunchWithFaireApplication } from "@faire/web-api/indigofair/data/IRetailerLaunchWithFaireApplication";
import { useStrictLocalization } from "@faire/web/common/localization";

import {
  OwFFormFieldKey,
  OwFFormFieldType,
} from "@faire/retailer/components/OpenWithFaire/OpenWithFaireApplication/consts";
import {
  IOwFFormFieldSchema,
  OwFFormFieldValueType,
} from "@faire/retailer/components/OpenWithFaire/OpenWithFaireApplication/OwFFormField";

import {
  getVisibleFieldKeys,
  useBuildResidentialAddressSchema,
} from "./helpers";
import { useIsSoftCreditCheckApplication } from "./useIsSoftCreditCheckApplication";
import { useValidatePartialFormData } from "./useValidatePartialFormData";

interface IUseBasicInfo {
  formData: IRetailerLaunchWithFaireApplication;
  setValue: (
    fieldName: OwFFormFieldKey,
    newValue: OwFFormFieldValueType
  ) => void;
  isResidentialAddressFormVisible: boolean;
  toggleResidentialAddressForm: () => void;
  isSignUp?: boolean;
}

export const useBasicInfo = ({
  formData,
  setValue,
  isResidentialAddressFormVisible,
  toggleResidentialAddressForm,
  isSignUp,
}: IUseBasicInfo) => {
  const { strictLocalize } = useStrictLocalization();
  const { buildResidentialAddressSchema } = useBuildResidentialAddressSchema();
  const { validatePartialFormData } = useValidatePartialFormData();

  const requiredBasicFieldKeys = useBasicInfoRequiredKeys({
    isSignUp,
  });

  const visibleBasicInfoKeys: OwFFormFieldKey[] = getVisibleFieldKeys(
    ["full_name", "residential_address"],
    requiredBasicFieldKeys
  );

  const getBasicInfoErrors = () =>
    validatePartialFormData({
      formData,
      keysToValidate: requiredBasicFieldKeys,
      isResidentialAddressFormVisible,
    });

  const basicInfoFields: IOwFFormFieldSchema[] = visibleBasicInfoKeys.map(
    (key) => {
      switch (key) {
        case "residential_address":
          return buildResidentialAddressSchema({
            key,
            isResidentialAddressFormVisible,
            setValue,
            toggleResidentialAddressForm,
          });
        case "full_name":
        default:
          return {
            fieldType: OwFFormFieldType.INPUT,
            fieldName: "full_name",
            labelText: strictLocalize({
              defaultMessage: "What's your full name?",
              description: {
                text: "Question in Open with Faire application form",
              },
            }),
            required: true,
          };
      }
    }
  );

  return {
    visibleBasicInfoKeys,
    getBasicInfoErrors,
    basicInfoFields,
  };
};

const useBasicInfoRequiredKeys = ({
  isSignUp,
}: {
  isSignUp: boolean | undefined;
}): OwFFormFieldKey[] => {
  const { isSoftCreditCheckApplication } = useIsSoftCreditCheckApplication();

  if (isSoftCreditCheckApplication) {
    return ["residential_address"];
  }
  if (!isSignUp) {
    return ["full_name", "residential_address"];
  }
  return [];
};
